// Libs
import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/Option';
import { find } from 'lodash';
import * as R from 'ramda';

// Constants
import { EMPTY_CONFERENCE_ADDRESS } from '@domui-domain/districts/conferences/constants';
import { CreateConferenceFormResult } from '@domui-domain/districts/types';
import { getFormattedDate } from '@domui-utils/datetime';

import {
  Country,
  InputCreateConference,
  MeetingType,
  PhoneType,
  TimeZone,
} from './types';

const getCountryNameByCode = (
  countryCode: string | null,
  countries: Country[]
) =>
  pipe(
    countries,
    A.filter(({ id }) => id === countryCode),
    A.head,
    O.mapNullable(R.prop('countryName')),
    O.getOrElse(() => '')
  );

export const mapFormResultToCreateConferenceInput = (
  {
    districtId,
    nfKey,
    dateTime: { startDate, startTime, endDate, endTime, timeZone },
    rotaryYear,
    venueName,
    language,
    prAccommodation,
    locationNameEng,
    locationCityStateEng,
    presRepresentativeHotel,
    presRepresentativeHotelAddress,
    presRespresntativeHotelPhone,
    prComment,
    govComment,
    venueType,
    onlineLocation,
    locationNameOrDetail,
    detailForJoiningRemotely,
    conferenceAddress: {
      city,
      country: conferenceCountryCode,
      lineOne,
      lineTwo,
      lineThree,
      postalCode,
      internationalProvince,
      state,
    },
    prAddress: {
      city: prCity,
      country: prCountry,
      internationalProvince: prInternationalProvince,
      lineOne: prLineOne,
      lineThree: prLineThree,
      lineTwo: prLineTwo,
      postalCode: prPostalCode,
      state: prState,
    },
    isPresRepRequested,
    presidentialRepresentativeId,
    participatingDistricts,
    accomodationPhone: { number, countryId },
  }: CreateConferenceFormResult,
  formattedCountries: Country[],
  timeZones?: TimeZone[],
  originalTimeZone?: string
): InputCreateConference => {
  const isOnline = venueType === MeetingType.Online;
  const isPhysical = venueType === MeetingType.Physical;
  const conferenceCountryValue = getCountryNameByCode(
    conferenceCountryCode || null,
    formattedCountries
  );
  const presRepCountryValue = getCountryNameByCode(
    prCountry || '',
    formattedCountries
  );

  // If user deletes startTime & endTime and changes timeZone value
  // We should save the same value that was before editing
  let tz = timeZone;
  if (originalTimeZone && !(startTime && endTime)) {
    tz = timeZone;
  }

  const timeZoneValue =
    find(timeZones, ({ id, value }) => id === tz || value === tz)
      ?.timeZoneName || null;

  return {
    districtId,
    nfKey,
    startDate: startDate ? getFormattedDate(startDate) : '',
    startTime,
    endDate: endDate ? getFormattedDate(endDate) : '',
    endTime,
    rotaryYear,
    venueName: !isOnline ? venueName : '',
    language,
    prAccommodation: isPresRepRequested && !isOnline ? prAccommodation : '',
    locationNameEng,
    locationCityStateEng,
    presRepresentativeHotel,
    presRepresentativeHotelAddress,
    presRespresntativeHotelPhone,
    prComment,
    govComment,
    venueType,
    locationNameOrDetail,
    conferenceAddress: !isOnline
      ? {
          city,
          country: conferenceCountryValue,
          lineOne,
          lineTwo,
          lineThree,
          postalCode,
          internationalProvince,
          state,
        }
      : EMPTY_CONFERENCE_ADDRESS,
    locationCityState: city,
    locationCountry: conferenceCountryValue,
    prAddress:
      !isOnline && isPresRepRequested
        ? {
            city: prCity,
            country: presRepCountryValue,
            internationalProvince: prInternationalProvince,
            lineOne: prLineOne,
            lineThree: prLineThree,
            lineTwo: prLineTwo,
            postalCode: prPostalCode,
            state: prState,
          }
        : null,
    isPresRepRequested,
    presRepAccPhone:
      !isOnline && isPresRepRequested && number
        ? {
            number,
            phoneType: PhoneType.Mobile,
            countryId: countryId || '',
            extension: '',
          }
        : null,

    participatingDistrict: {
      id: '',
      districtId,
      nfKey: '',
      isPrimary: true,
    },
    presidentialRepresentativeId,
    timeZone: timeZoneValue,
    //  comments,
    participatingDistricts: participatingDistricts?.map(({ id, value }) => ({
      id,
      riDistrictId: value,
    })),
    // Used for Online & Hybrid type
    ...(isPhysical
      ? {
          onlineLocation: '',
          detailForJoiningRemotely: '',
        }
      : { onlineLocation, detailForJoiningRemotely }),
  };
};
